import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { StatWidget } from '@/components/widgets/StatWidget';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useToast } from '@/components/common/toast';
import { formatNumber } from '@/utils/format';
import { feedMonitoringApi } from '@/api/services/feedMonitoring';
import { useFeedMonitoringLive } from './useFeedMonitoringLive';
import type { FeedMonitoringLiveDto } from '@/api/types/feedMonitoring';

const rowId = (r: FeedMonitoringLiveDto) => `${r.symbol}|${r.dataSourceId}`;

// moment.parseZone keeps the wire offset (no local TZ shift), matching legacy
// FeedMonitoringLive.formatDate.
function fmtTickTime(iso: string | null): string {
  if (!iso) return 'N/A';
  const m = moment.parseZone(iso);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm:ss') : 'N/A';
}
function tickSortValue(iso: string | null): number {
  if (!iso) return 0;
  const m = moment.parseZone(iso);
  return m.isValid() ? m.valueOf() : 0;
}

function renderPrice(value: number | null | undefined, digits: number | null): string {
  if (value == null || (value as unknown) === '') return 'N/A';
  if (digits == null) return String(value);
  return formatNumber(value, digits);
}

const isBlocked = (r: FeedMonitoringLiveDto) => r.inSession === false || r.isStaleOverLimit;

// bucket: stale first, then rows with an allowed lag set, then the rest.
function bucket(r: FeedMonitoringLiveDto): number {
  if (r.isStale) return 1;
  return r.allowedLagSeconds > 0 ? 2 : 3;
}
function sortLiveRows(rows: FeedMonitoringLiveDto[]): FeedMonitoringLiveDto[] {
  return [...rows].sort((a, b) => {
    const ba = bucket(a);
    const bb = bucket(b);
    if (ba !== bb) return ba - bb;
    if (ba === 1) return b.lagSeconds - a.lagSeconds;
    return a.symbol.localeCompare(b.symbol);
  });
}

interface Kpis {
  highest: string;
  highlightedCount: number;
  worstConnector: string;
}
function computeKpis(rows: FeedMonitoringLiveDto[]): Kpis {
  let highest: FeedMonitoringLiveDto | null = null;
  let highlightedCount = 0;
  const connectorScores: Record<string, { totalLag: number; count: number }> = {};

  for (const r of rows) {
    if (r.isStale) highlightedCount++;
    if (isBlocked(r)) continue;
    if (!highest || r.lagSeconds > highest.lagSeconds) highest = r;
    const score = connectorScores[r.source] ?? { totalLag: 0, count: 0 };
    score.totalLag += r.lagSeconds || 0;
    score.count += 1;
    connectorScores[r.source] = score;
  }

  let worst = '-';
  let worstAvg = -Infinity;
  let worstName = '';
  let worstCount = 0;
  for (const [name, s] of Object.entries(connectorScores)) {
    if (s.count === 0) continue;
    const avg = s.totalLag / s.count;
    if (avg > worstAvg) {
      worstAvg = avg;
      worstName = name;
      worstCount = s.count;
    }
  }
  if (worstName) worst = `${worstName} — ${worstCount} symbols — avg ${worstAvg.toFixed(1)}s`;

  return {
    highest: highest ? `${highest.symbol} — ${highest.source} — ${highest.lagSeconds.toFixed(1)}s` : '-',
    highlightedCount,
    worstConnector: worst,
  };
}

export function LiveMonitoringTab() {
  const toast = useToast();

  const [rows, setRows] = useState<FeedMonitoringLiveDto[]>([]);
  const [kpis, setKpis] = useState<Kpis>({ highest: '-', highlightedCount: 0, worstConnector: '-' });
  const [firstDataReceived, setFirstDataReceived] = useState(false);

  // Inline edit state.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editLag, setEditLag] = useState<string>('');
  const [editAlerts, setEditAlerts] = useState<boolean>(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const editingKeyRef = useRef<string | null>(null);
  editingKeyRef.current = editingKey;
  const isRefreshing = useRef(false);
  const lastWarning = useRef('');

  const refreshLive = useCallback(async () => {
    if (isRefreshing.current) return;
    isRefreshing.current = true;
    try {
      const res = await feedMonitoringApi.getLiveMonitoring();
      const items = res.items ?? [];

      // KPIs always recompute from the latest push.
      setKpis(computeKpis(items));

      // Edit-lock: never rebuild the grid while a row is being edited.
      if (editingKeyRef.current == null) setRows(sortLiveRows(items));

      // Connector warning only on change (dedup).
      const warn = res.warning ?? '';
      if (warn !== lastWarning.current) {
        lastWarning.current = warn;
        if (warn) toast.warning(warn);
      }

      setFirstDataReceived(true);
    } catch {
      // keep the current snapshot; a later tick will retry
    } finally {
      isRefreshing.current = false;
    }
  }, [toast]);

  const { status } = useFeedMonitoringLive({
    enabled: true,
    onChanged: () => void refreshLive(),
    onError: () => void refreshLive(),
  });

  // No-realtime fallback: if the hub can't connect, still pull one snapshot.
  useEffect(() => {
    if (status === 'disconnected' && !firstDataReceived) void refreshLive();
  }, [status, firstDataReceived, refreshLive]);

  const startEdit = (r: FeedMonitoringLiveDto) => {
    setEditingKey(rowId(r));
    setEditLag(String(r.allowedLagSeconds));
    setEditAlerts(r.isMonitored);
  };
  const cancelEdit = () => {
    setEditingKey(null);
    setEditLag('');
  };

  const saveRow = async (r: FeedMonitoringLiveDto) => {
    const parsed = parseFloat(String(editLag).replace(/,/g, ''));
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error('Enter a non-negative number for Allowed Lag');
      return;
    }
    setBusyKey(rowId(r));
    try {
      await feedMonitoringApi.saveSetting({ symbol: r.symbol, dataSourceId: r.dataSourceId, allowedLagSeconds: parsed });
      await feedMonitoringApi.saveSettingAlerts({ symbol: r.symbol, dataSourceId: r.dataSourceId, isEnabled: editAlerts });
      setRows((rs) => rs.map((x) => (rowId(x) === rowId(r) ? { ...x, allowedLagSeconds: parsed, isMonitored: editAlerts } : x)));
      setEditingKey(null);
      setEditLag('');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast.error(msg || 'Failed to save the setting.');
    } finally {
      setBusyKey(null);
    }
  };

  // Standalone alerts toggle (not in edit mode).
  const toggleAlerts = async (r: FeedMonitoringLiveDto, isEnabled: boolean) => {
    // Optimistic: reflect the flip immediately, revert on failure.
    setRows((rs) => rs.map((x) => (rowId(x) === rowId(r) ? { ...x, isMonitored: isEnabled } : x)));
    try {
      await feedMonitoringApi.saveSettingAlerts({ symbol: r.symbol, dataSourceId: r.dataSourceId, isEnabled });
    } catch {
      setRows((rs) => rs.map((x) => (rowId(x) === rowId(r) ? { ...x, isMonitored: !isEnabled } : x)));
      toast.error('Failed to update alerts setting');
    }
  };

  const columns = useMemo<DataGridColumn<FeedMonitoringLiveDto>[]>(() => [
    { key: 'symbol', title: 'Symbol', className: 'text-start', value: (r) => r.symbol },
    { key: 'source', title: 'Source', className: 'text-start', value: (r) => r.source },
    { key: 'bid', title: 'Bid', className: 'text-end', sortable: false, filterable: false, value: (r) => r.bid, cell: (r) => renderPrice(r.bid, r.digits) },
    { key: 'ask', title: 'Ask', className: 'text-end', sortable: false, filterable: false, value: (r) => r.ask, cell: (r) => renderPrice(r.ask, r.digits) },
    {
      key: 'lastTickTime',
      title: 'Last Price Update',
      className: 'text-start',
      value: (r) => tickSortValue(r.lastTickTime),
      cell: (r) => fmtTickTime(r.lastTickTime),
    },
    {
      key: 'lagSeconds',
      title: 'Lag (s)',
      className: 'text-end',
      filterable: false,
      value: (r) => (isBlocked(r) ? -1 : r.lagSeconds),
      cell: (r) => {
        if (isBlocked(r)) {
          const title = r.inSession === false ? 'Outside trading session' : 'No price update today (possible feed issue)';
          return <span title={title}>N/A</span>;
        }
        return formatNumber(r.lagSeconds, 1);
      },
    },
    { key: 'sessionTimes', title: 'Session Times', className: 'text-start', sortable: false, value: (r) => r.sessionTimes ?? '-', cell: (r) => r.sessionTimes || '-' },
    {
      key: 'allowedLagSeconds',
      title: 'Allowed Lag (s)',
      className: 'text-end',
      filterable: false,
      value: (r) => r.allowedLagSeconds,
      cell: (r) =>
        editingKey === rowId(r) ? (
          <input
            type="number"
            min={0}
            step="any"
            className="form-control form-control-sm text-end"
            style={{ maxWidth: 110, display: 'inline-block' }}
            value={editLag}
            disabled={busyKey === rowId(r)}
            onChange={(e) => setEditLag(e.target.value)}
          />
        ) : (
          formatNumber(r.allowedLagSeconds, 1)
        ),
    },
    {
      key: 'isMonitored',
      title: 'Alerts',
      className: 'text-center',
      sortable: false,
      filterable: false,
      value: (r) => (r.isMonitored ? 'Yes' : 'No'),
      cell: (r) => {
        const editing = editingKey === rowId(r);
        const checked = editing ? editAlerts : r.isMonitored;
        return (
          <input
            className="form-check-input alerts-toggle"
            type="checkbox"
            role="switch"
            checked={checked}
            disabled={busyKey === rowId(r)}
            onChange={(e) => {
              if (editing) setEditAlerts(e.target.checked);
              else void toggleAlerts(r, e.target.checked);
            }}
          />
        );
      },
    },
  ], [editingKey, editLag, editAlerts, busyKey]);

  return (
    <>
      <div className="card mb-3 bg-light">
        <div className="card-body p-3 fs-12 text-muted lh-lg">
          When a symbol has an Allowed Lag set and Alerts is ON, a notification fires the moment its lag exceeds that
          threshold. If the lag remains above the threshold, the same alert re-fires every 1 hour until it recovers.
        </div>
      </div>

      <div className="row row-cols-1 row-cols-md-3 g-2 mb-3">
        <div className="col">
          <StatWidget label="Highest Lag" value={kpis.highest} icon="fa fa-exclamation-triangle" theme="danger" />
        </div>
        <div className="col">
          <StatWidget label="Highlighted Symbols" value={kpis.highlightedCount} icon="fa fa-bolt" theme="warning" />
        </div>
        <div className="col">
          <StatWidget label="Highest-Lag Connector" value={kpis.worstConnector} icon="fa fa-plug" theme="info" />
        </div>
      </div>

      {!firstDataReceived ? (
        <div className="text-center text-muted py-5">
          <i className="fa fa-spinner fa-spin me-2" />Connecting to live data…
        </div>
      ) : (
        <DataGrid
          columns={columns}
          data={rows}
          rowKey={rowId}
          pageSize={5}
          card
          columnSearch
          includeExcel
          excelFileName="Feed Monitoring Live"
          rowClassName={(r) => (r.isStale ? 'feed-stale-row' : undefined)}
          emptyMessage="No symbols are being monitored."
          rowActions={(r) =>
            editingKey === rowId(r) ? (
              <span className="d-inline-flex gap-1">
                <button type="button" className="btn btn-outline-primary btn-sm" title="Save" onClick={() => void saveRow(r)} disabled={busyKey === rowId(r)}>
                  {busyKey === rowId(r) ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-check" />}
                </button>
                <button type="button" className="btn btn-outline-danger btn-sm" title="Cancel" onClick={cancelEdit} disabled={busyKey === rowId(r)}>
                  <i className="fa fa-times" />
                </button>
              </span>
            ) : (
              <button type="button" className="btn btn-outline-primary btn-sm" title="Edit" onClick={() => startEdit(r)}>
                <i className="fa fa-edit" />
              </button>
            )
          }
        />
      )}
    </>
  );
}
